import { CONSOLE_ROUTES } from "../identity/console/live";
import { parseInbox } from "../identity/console/schemas";
import type { Http } from "../identity/live/http";
import { IdentityServiceError } from "../identity/live/errors";
import { PermissionError } from "../identity/errors";
import type { Member } from "../identity/types";
import type { SessionPort } from "../mock/session-port";
import { parseSiteView } from "../site/schemas";
import { FIRST_SAMPLE_ID } from "../samples";
import { startingDrawings } from "../starting-drawings";
import { projectType } from "../taxonomy";
import {
  AccessDeniedError,
  NotFoundError,
  type OrthosApi,
  type Person,
  type ProjectMember,
} from "../types";
import { toDetail, toNotifications, toSummary } from "./map";
import {
  parseDetail,
  parseDrawing,
  parseMembers,
  parseProject,
  parseProjects,
  parseRestriction,
  parseRestrictions,
  parseSelfAdds,
} from "./schemas";

/**
 * OrthosApi on the identity service's projects (VITE_PROJECTS_MODE=live; WS-29). Every call works in the organisation
 * the session is in, and the service decides who sees and changes what (27 §5.3.1). Notifications are the person's
 * in-app inbox (`/me/notifications`, FR-PROJ-080, 082); the centre lists those about a project. Sample projects are
 * the service's (`/samples`, FR-PROJ-009).
 */

export interface LiveProjectsOptions {
  http: Http;
  /** Who is signed in, in which organisation (the live session). */
  session: SessionPort;
  /** The organisation's members, for avatars and the wizard's team; guests may not list them. */
  members: () => Promise<Member[]>;
  /** How many files the project holds, as the person sees them (the files service's, not identity's). */
  fileCount: (projectId: string) => Promise<number>;
}

/** The header that carries the wizard's key (FR-PROJ-054). */
const IDEMPOTENCY_HEADER = "Idempotency-Key";
const SAMPLES_PATH = "/samples";
const NOT_A_SAMPLE = "Only sample projects can be removed; archive real projects instead.";

const enc = encodeURIComponent;
const projectPath = (id: string) => `/projects/${enc(id)}`;

/** A refusal about one project as the pages handle it: no access offers "Request access" (FR-PROJ-116). */
function aboutProject(id: string): (error: unknown) => never {
  return (error) => {
    if (error instanceof PermissionError) throw new AccessDeniedError(id);
    if (error instanceof IdentityServiceError && error.code === "not_found")
      throw new NotFoundError(`Project ${id}`);
    throw error;
  };
}

function toMember(row: {
  userId: string;
  role: ProjectMember["role"];
  name: string | null;
  email: string | null;
}): ProjectMember {
  return {
    userId: row.userId,
    role: row.role,
    name: row.name ?? undefined,
    email: row.email ?? undefined,
  };
}

function person(member: Member): Person {
  return {
    id: member.userId,
    name: member.name,
    initials: member.initials,
    colour: member.colour,
    title: "",
    email: member.email,
  };
}

export function createLiveProjectsApi({
  http,
  session,
  members,
  fileCount,
}: LiveProjectsOptions): OrthosApi {
  const organisation = async () => session.current().organisation;
  return {
    organisation,
    async me() {
      return session.current().person;
    },
    async people() {
      const me = session.current().person;
      try {
        return (await members()).map(person);
      } catch (error) {
        if (error instanceof PermissionError) return [me];
        throw error;
      }
    },
    async listProjects() {
      return (await http.get("/projects", parseProjects)).map((row) => toSummary(row));
    },
    async getProject(id) {
      // The count and the site record come from their own services; a failure there leaves the project open with
      // nothing counted and no site facts, as a project with neither.
      const [detail, count, site] = await Promise.all([
        http.get(projectPath(id), parseDetail).catch(aboutProject(id)),
        fileCount(id).catch(() => 0),
        http
          .get(`${projectPath(id)}/site`, parseSiteView)
          .then((view) => view.record)
          .catch(() => undefined),
      ]);
      return toDetail(detail, { fileCount: count, site });
    },
    async createProject(input, key) {
      const drawings = input.drawings ?? startingDrawings(projectType(input.type).discipline);
      // The pin is saved into the project's site after creation (the wizard does it), not sent with the project.
      const { location, ...project } = input;
      void location;
      const body = { ...project, drawings };
      const made = await http.send("POST", "/projects", body, parseProject, {
        [IDEMPOTENCY_HEADER]: key,
      });
      return toSummary(made);
    },
    async updateProject(id, patch) {
      const changed = await http
        .send("PATCH", projectPath(id), patch, parseProject)
        .catch(aboutProject(id));
      return toSummary(changed);
    },
    async ensureDrawing(projectId, drawing) {
      const path = `${projectPath(projectId)}/drawings/${enc(drawing.id)}`;
      // A refusal keeps the service's words ("Editor role needed to add drawings."): Draft shows them.
      await http.send("PUT", path, { name: drawing.name, kind: drawing.kind }, parseDrawing);
    },
    async setPinned(id, pinned) {
      await http.sendOnly("PUT", `${projectPath(id)}/pin`, { pinned }).catch(aboutProject(id));
    },
    async setArchived(id, archived) {
      const path = `${projectPath(id)}/${archived ? "archive" : "restore"}`;
      await http.send("POST", path, {}, parseProject).catch(aboutProject(id));
    },
    projectPeople: {
      async selfAdds(projectId) {
        const rows = await http.get(`${projectPath(projectId)}/self-adds`, parseSelfAdds);
        return rows.map(({ id, userId, name, reason, at }) => ({ id, userId, name, reason, at }));
      },
      async list(projectId) {
        return (
          await http
            .get(`${projectPath(projectId)}/members`, parseMembers)
            .catch(aboutProject(projectId))
        ).map(toMember);
      },
      async set(projectId, userId, role) {
        const path = `${projectPath(projectId)}/members/${enc(userId)}`;
        return (await http.send("PUT", path, { role }, parseMembers)).map(toMember);
      },
      async remove(projectId, userId) {
        await http.sendOnly(
          "DELETE",
          `${projectPath(projectId)}/members/${enc(userId)}`,
          undefined,
        );
        return (await http.get(`${projectPath(projectId)}/members`, parseMembers)).map(toMember);
      },
    },
    drawingAccess: {
      async list(projectId) {
        return http
          .get(`${projectPath(projectId)}/restrictions`, parseRestrictions)
          .catch(aboutProject(projectId));
      },
      async restrict(projectId, itemId, rule) {
        const path = `${projectPath(projectId)}/restrictions/${enc(itemId)}`;
        return http.send("PUT", path, rule, parseRestriction);
      },
      async lift(projectId, itemId) {
        await http.sendOnly(
          "DELETE",
          `${projectPath(projectId)}/restrictions/${enc(itemId)}`,
          undefined,
        );
      },
    },
    async addSample(sampleId = FIRST_SAMPLE_ID) {
      const added = await http.send("POST", `${SAMPLES_PATH}/${enc(sampleId)}`, {}, parseProject);
      return toSummary(added);
    },
    async removeSample(id) {
      const { sample } = await http.get(projectPath(id), parseDetail).catch(aboutProject(id));
      if (sample === null || sample === undefined) throw new Error(NOT_A_SAMPLE);
      await http.sendOnly("DELETE", `${SAMPLES_PATH}/${enc(sample)}`, undefined);
    },
    // The one in-app inbox (FR-PROJ-080, 082), with what watchers are told (FR-COLLAB-148).
    async listNotifications() {
      const [inbox, projects] = await Promise.all([
        http.get(CONSOLE_ROUTES.inbox, parseInbox),
        http.get("/projects", parseProjects),
      ]);
      return toNotifications(inbox.notifications, projects);
    },
    async setNotificationRead(id, read) {
      // The inbox has no "unread" route: marking one unread changes only this view until it reloads.
      if (read) await http.sendOnly("POST", CONSOLE_ROUTES.readOne(id), undefined);
    },
    async markAllNotificationsRead() {
      await http.sendOnly("POST", CONSOLE_ROUTES.readAll, undefined);
    },
  };
}
